import { Music2, Play, Pause, SkipBack, SkipForward, Volume2, VolumeX } from "lucide-react";
import { usePlayer } from "../../context/PlayerContext";
import { useLanguage } from "../../context/LanguageContext";
import { Equalizer } from "./Equalizer";
const fmt = seconds => {
  if (!Number.isFinite(seconds) || !seconds) return "0:00";
  return `${Math.floor(seconds / 60)}:${Math.floor(seconds % 60).toString().padStart(2, "0")}`;
};
const Control = ({ testId, label, onClick, children }) => {
  const { t } = useLanguage();
  return <button data-testid={testId} onClick={onClick} aria-label={t(label)} title={t(label)} className="text-slate-400 transition-colors duration-200 hover:text-[#FFC857]">{children}</button>;
};
const PlayButton = ({ mobile = false }) => {
  const { t } = useLanguage();
  const { isPlaying, toggle } = usePlayer();
  return <button data-testid={mobile ? "player-mobile-play-pause-button" : "player-play-pause-button"} onClick={toggle}
    className="flex h-11 w-11 shrink-0 items-center justify-center rounded-full bg-[#E5A93C] text-[#0B0C10] transition-colors hover:bg-[#FFC857]" aria-label={t(isPlaying ? "Pause" : "Play")}>
    {isPlaying ? <Pause size={18} /> : <Play size={18} className="ml-0.5" />}
  </button>;
};
export const PlayerControls = () => {
  const { t } = useLanguage();
  const player = usePlayer();
  const { track, time, duration, volume, muted } = player;
  return <div className="border-b border-[#282C3D] p-5">
    <div className="flex items-center gap-3">
      <div className="flex h-12 w-12 shrink-0 items-center justify-center rounded-xl bg-[#1A1D2B] text-[#E5A93C]"><Music2 size={20} /></div>
      <div className="min-w-0"><p data-testid="player-current-track-title" className="truncate text-sm font-semibold text-slate-100">{track?.title}</p><p className="font-mono text-[10px] uppercase tracking-widest text-slate-500">{track?.style}</p></div>
    </div>
    <input data-testid="player-seek-slider" type="range" min={0} max={duration || 0} step={0.1} value={time} onChange={event => player.seek(Number(event.target.value))} className="studio-range mt-4 w-full" aria-label={t("Seek")} />
    <div className="mt-1 flex justify-between font-mono text-[10px] text-slate-500"><span data-testid="player-current-time">{fmt(time)}</span><span data-testid="player-duration">{fmt(duration)}</span></div>
    <div className="mt-3 flex items-center justify-center gap-4">
      <Control testId="player-prev-track-button" onClick={player.prev} label="Previous track"><SkipBack size={18} /></Control>
      <PlayButton />
      <Control testId="player-next-track-button" onClick={player.next} label="Next track"><SkipForward size={18} /></Control>
    </div>
    <div className="mt-4 flex items-center gap-3">
      <Control testId="player-mute-button" onClick={() => player.setMuted(!muted)} label={muted ? "Unmute" : "Mute"}>{muted ? <VolumeX size={16} /> : <Volume2 size={16} />}</Control>
      <input data-testid="player-volume-slider" type="range" min={0} max={1} step={0.01} value={muted ? 0 : volume} onChange={event => { player.setVolume(Number(event.target.value)); player.setMuted(false); }} className="studio-range flex-1" aria-label={t("Volume")} />
    </div>
  </div>;
};
export const MobilePlayer = () => {
  const { t } = useLanguage();
  const { track, isPlaying, next, error } = usePlayer();
  return <div data-testid="player-mobile-bar" className="fixed bottom-0 left-0 right-0 z-40 border-t border-[#282C3D] bg-[#0E1018]/95 px-4 py-3 backdrop-blur-md lg:hidden">
    {error && <p role="alert" data-testid="player-mobile-error" className="mb-2 text-xs text-rose-300">{t(error)}</p>}
    <div className="flex items-center gap-3"><PlayButton mobile />
      <div className="min-w-0 flex-1"><p className="truncate text-sm font-medium text-slate-100">{track?.title}</p><p className="font-mono text-[10px] uppercase tracking-widest text-slate-500">{track?.style}</p></div>
      <Equalizer playing={isPlaying} bars={4} />
      <Control testId="player-mobile-next-button" onClick={next} label="Next track"><SkipForward size={18} /></Control>
    </div>
  </div>;
};